import * as React from 'react';
import Form from 'antd/lib/form';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import Select from 'antd/lib/select';
import { antGrey, runwayColors } from '../../styles/globalStyles';
import { Flex } from 'antd/lib';
import { IntegrationType } from '@/types';
import type { Nullable } from '@/typescriptHelpers';

type TestCasesFilterSettingsProps = {
    parentFieldPath: (string | number)[];
    integrationType: Nullable<IntegrationType>;
};

export const TestCasesFilterSettings = (props: TestCasesFilterSettingsProps) => {
    const Component = React.useMemo(() => {
        switch (props.integrationType) {
            case IntegrationType.BrowserStack:
                return BrowserStackTestCasesFilterSettings;
            default:
                return null;
        }
    }, [props.integrationType]);

    if (!Component) {
        return null;
    }

    return (
        <div
            style={{
                marginLeft: 16,
                padding: 20,
                borderRadius: 8,
                border: `1px solid ${antGrey.grey5}`,
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
            }}
        >
            <Flex vertical gap={8}>
                <Text strong>Test case filters</Text>
                <Text style={{ color: antGrey.grey7, paddingBottom: 2 }}>
                    When creating test plans, the filters below will be applied to determine which test cases are
                    included in the plan.
                </Text>
                <Component {...props} />
            </Flex>
        </div>
    );
};

const BrowserStackTestCasesFilterSettings = ({ parentFieldPath }: TestCasesFilterSettingsProps) => {
    const BROWSER_STACK_DEFAULT_CASE_TYPES = [
        'Acceptance',
        'Accessibility',
        'Compatibility',
        'Destructive',
        'Functional',
        'Performance',
        'Regression',
        'Security',
        'Smoke & Sanity',
        'Usability',
        'Other',
    ].map((v) => ({ value: v, label: v }));

    return (
        <Flex vertical>
            <Form.Item label="Case types" name={[...parentFieldPath, 'caseTypes']}>
                <Select
                    mode="multiple"
                    options={BROWSER_STACK_DEFAULT_CASE_TYPES}
                    placeholder="Add one or more case types"
                    style={{ width: '100%' }}
                />
            </Form.Item>
            <Form.List name={[...parentFieldPath, 'customFields']}>
                {(fields, { add, remove }) => {
                    return (
                        <Space direction="vertical">
                            <Text>Custom fields</Text>
                            <Space direction="vertical">
                                {fields.map((field) => (
                                    <Flex key={field.key}>
                                        <Space direction="horizontal" align="start">
                                            <Form.Item
                                                rules={[{ required: true, message: 'Name is required' }]}
                                                name={[field.name, 'name']}
                                                style={{ marginBottom: 0, minWidth: 150 }}
                                            >
                                                <Input placeholder="Field name" />
                                            </Form.Item>

                                            <Form.Item
                                                rules={[{ required: true, message: 'Value is required' }]}
                                                name={[field.name, 'value']}
                                                style={{ marginBottom: 0, minWidth: 200 }}
                                            >
                                                <Select
                                                    mode="tags"
                                                    placeholder="Field values"
                                                    notFoundContent="Add one or more values"
                                                />
                                            </Form.Item>
                                        </Space>
                                        <Button
                                            danger
                                            type="link"
                                            icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                                            onClick={() => remove(field.name)}
                                        />
                                    </Flex>
                                ))}
                            </Space>
                            <Button
                                icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                onClick={() => add()}
                            >
                                Add custom field
                            </Button>
                        </Space>
                    );
                }}
            </Form.List>
        </Flex>
    );
};
